import { useState, useEffect } from 'preact/hooks';

// Timer hook for PST timezone
function useTimer(endDate, template) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);
  useEffect(() => {
    if (!endDate) return undefined;

    const calculateTimeLeft = () => {
      const parseCustomDate = (dateStr) => {
        const match = dateStr.match(
          /^(\d{4})-(\d{2})-(\d{2})-(\d{2}):(\d{2})$/,
        );
        if (!match) {
          console.error('Invalid date format. Expected: YYYY-MM-DD-HH:MM');
          return null;
        }
        const [, year, month, day, hour, minute] = match;

        const dateString = `${year}-${month}-${day} ${hour}:${minute}:00`;
        const options = {
          timeZone: 'America/Los_Angeles',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        };

        const parts = dateString.match(
          /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/,
        );
        if (!parts) return null;

        const testDate = new Date(
          `${year}-${month}-${day}T${hour}:${minute}:00`,
        );
        const pstString = testDate.toLocaleString('en-US', options);
        const offset = testDate.getTime() - new Date(pstString).getTime();

        return new Date(testDate.getTime() + offset);
      };

      const targetDate = parseCustomDate(endDate);
      if (!targetDate) return;

      const now = new Date();
      const isGold = template === 'gld';

      let difference;

      if (isGold) {
        // Gold template: Show same countdown in all timezones (store opening approach)
        // Get the PST time components from targetDate
        const targetPstString = targetDate.toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });

        // Parse PST string (format: "MM/DD/YYYY, HH:MM:SS")
        const pstMatch = targetPstString.match(
          /(\d+)\/(\d+)\/(\d+),?\s+(\d+):(\d+):(\d+)/,
        );

        if (pstMatch) {
          const [, month, day, year, hour, minute, second] = pstMatch;

          // Create a date with these time components in the USER'S local timezone
          const targetInUserTz = new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10),
            parseInt(hour, 10),
            parseInt(minute, 10),
            parseInt(second, 10),
          );

          difference = targetInUserTz.getTime() - now.getTime();
        } else {
          difference = 0;
        }
      } else {
        // SPD template: Respect timezone - immediate drop across all timezones
        // Use absolute time difference (target PST time vs user's local time)
        difference = targetDate.getTime() - now.getTime();
      }

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        setTimeLeft({ days, hours, minutes, seconds });
        setIsExpired(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsExpired(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate, template]);

  return { timeLeft, isExpired };
}

export default useTimer;
